import React, { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { periodTotals, goalProgress, formatMetric } from '@battleops/shared';
import { useMetrics, useMetricCtx, rowsFor } from '@battleops/client/src/data/useMetrics';
import Icon from './Icon';
import s from './Metrics.module.css';

const HOLD_MS = 500;

/** One counter's numbers for a tile / the Track list: today, the goal meter. */
export function useCounterRows(def) {
  const { values, personalValues } = useMetrics();
  const ctx = useMetricCtx();
  return useMemo(() => (def ? rowsFor(def, { values, personalValues, ctx, personal: true }) : []), [def, values, personalValues, ctx]);
}

// Home → your counters (CFO 4.4): one tile per personal counter marked "A tile on Home". Click = +1 (with an undo
// toast), hold or right-click = a quantity and a note. Automatic ones just show their number. Private to you.
export default function CounterTiles() {
  const { personal } = useMetrics();
  const tiles = personal.filter((m) => m.tile);
  const [asking, setAsking] = useState(null);
  if (!tiles.length) return null;
  return (
    <>
      <div className={s.tiles} aria-label="Your counters">
        {tiles.map((m) => <Tile key={m.id} def={m} onAsk={() => setAsking(m)} />)}
        <Link to="/track" className={`${s.tile} ${s.addTile}`}><Icon name="activity" size={16} />Track</Link>
      </div>
      {asking ? <QuantitySheet def={asking} onClose={() => setAsking(null)} /> : null}
    </>
  );
}

function Tile({ def, onAsk }) {
  const { log } = useMetrics();
  const rows = useCounterRows(def);
  const t = periodTotals(def, rows);
  const g = goalProgress(def, rows);
  const auto = def.source === 'auto';
  const held = useRef(null);
  const long = useRef(false);
  const down = () => { if (auto) return; long.current = false; held.current = setTimeout(() => { long.current = true; onAsk(); }, HOLD_MS); };
  const up = () => { clearTimeout(held.current); };
  const click = () => { if (auto || long.current) return; log({ metricId: def.id, personal: true }); };
  const tone = g ? (g.direction === 'down' ? (g.met ? 'good' : 'over') : g.met ? 'good' : null) : null;
  return (
    <button className={s.tile} data-auto={auto || undefined} onPointerDown={down} onPointerUp={up} onPointerLeave={up} onClick={click}
      onContextMenu={(e) => { if (auto) return; e.preventDefault(); onAsk(); }}
      title={auto ? 'Counted automatically' : 'Click +1 · hold or right-click for a number and a note'} aria-label={`${def.name}: ${t.day ?? 0} today${auto ? '' : '. Add one'}`}>
      <span className={s.tileTop}><span className={s.tileIcon}>{def.icon || '•'}</span>{def.name}</span>
      <span className={s.tileV}>{formatMetric({ ...def, unit: def.kind === 'count' ? null : def.unit }, t.day ?? 0)}</span>
      <span className={s.tileS}>{g ? `${g.direction === 'down' ? 'at most' : 'goal'} ${g.target} / ${g.period} · ${formatMetric({ unit: null }, g.value)} so far` : `today · ${t.week ?? 0} this week`}</span>
      {g ? <span className={s.meter} data-tone={tone || undefined}><span style={{ width: `${Math.min(100, g.pct || 0)}%` }} /></span> : null}
    </button>
  );
}

/** A quantity and a note for one counter (hold / right-click on a tile, or Log on Track). */
export function QuantitySheet({ def, onClose }) {
  const { log } = useMetrics();
  const [value, setValue] = useState('1');
  const [note, setNote] = useState('');
  const n = Number(String(value).replace(',', '.'));
  const save = () => { log({ metricId: def.id, personal: true, value: n, note: note.trim() || null }); onClose(); };
  return (
    <div className={s.scrim} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={s.sheet} role="dialog" aria-label={`Log ${def.name}`}>
        <div className="label">{def.icon} Log</div>
        <h2>{def.name}</h2>
        <div className="row" style={{ marginTop: 12 }}>
          <input className="input num" style={{ width: 140, fontSize: 20, fontWeight: 800 }} type="number" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} autoFocus onKeyDown={(e) => { if (e.key === 'Enter' && Number.isFinite(n)) save(); }} />
          {def.unit ? <span className="muted">{def.unit}</span> : null}
        </div>
        <input className="input" style={{ width: '100%', marginTop: 10 }} value={note} onChange={(e) => setNote(e.target.value)} placeholder="A note (optional)" />
        <div className="row" style={{ marginTop: 16 }}>
          <span className="spacer" />
          <button className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={value === '' || !Number.isFinite(n)} onClick={save}>Log it</button>
        </div>
      </div>
    </div>
  );
}
